import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Heart, Share2, Copy, Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, youtubeEmbedUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useWishlist } from "../lib/wishlist";
import { useCart } from "../lib/cart";
import { useSEO } from "../lib/seo";

const PLACEHOLDER =
  "/brand/cover-placeholder.png";

export default function VinylDetail() {
  const { t, lang } = useI18n();
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoIdx, setVideoIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const { has, toggle } = useWishlist();
  const { has: cartHas, add: cartAdd } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setVideoIdx(0);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/listings/${slug}`)
      .then((r) => setItem(r.data))
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft" && item.prev) navigate(`/catalogo/${item.prev.slug}`);
      if (e.key === "ArrowRight" && item.next) navigate(`/catalogo/${item.next.slug}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, navigate]);

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = item ? `${item.artist} — ${item.title}` : "";

  useSEO(item ? {
    title: `${item.artist} — ${item.title} · Maggie Records`,
    description: `${item.artist} · ${item.title}. ${[item.format, item.year, item.label].filter(Boolean).join(" · ")}. ${formatPrice(item.price, item.currency)}. Disponible en Maggie Records.`,
    image: item.cover_image || item.thumb,
    url: currentUrl,
    type: "product",
    jsonLd: {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: `${item.artist} — ${item.title}`,
      image: item.cover_image || item.thumb,
      description: `${item.artist} · ${item.title}. ${[item.format, item.year, item.label, item.catno].filter(Boolean).join(" · ")}.`,
      brand: { "@type": "Brand", name: item.label || "Maggie Records" },
      sku: String(item.listing_id),
      offers: {
        "@type": "Offer",
        url: currentUrl,
        priceCurrency: item.currency || "EUR",
        price: item.price,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/UsedCondition",
        seller: { "@type": "Organization", name: "Maggie Records" },
      },
    },
  } : {});

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-12 py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="aspect-square bg-[hsl(var(--muted))] animate-pulse" />
          <div className="space-y-4">
            <div className="h-6 w-1/3 bg-[hsl(var(--muted))] animate-pulse" />
            <div className="h-8 w-2/3 bg-[hsl(var(--muted))] animate-pulse" />
            <div className="h-24 bg-[hsl(var(--muted))] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-32" data-testid="detail-not-found">
        <p className="text-xs text-muted-foreground mb-3">404</p>
        <h1 className="text-2xl font-medium mb-5">{t("detail.notFound")}</h1>
        <Link to="/catalogo" className="text-sm vmr-link-underline">{t("detail.backLink")}</Link>
      </div>
    );
  }

  const cover = item.cover_image || item.thumb || PLACEHOLDER;
  const videos = item.release?.videos || [];
  const tracklist = item.release?.tracklist || [];
  const currentEmbed = videos[videoIdx] ? youtubeEmbedUrl(videos[videoIdx].uri) : null;
  const fav = has(item.listing_id);


  const shareText = encodeURIComponent(`${shareTitle} — ${formatPrice(item.price, item.currency)}`);
  const shareUrl = encodeURIComponent(currentUrl);
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${shareText}%20${shareUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
    telegram: `https://t.me/share/url?url=${shareUrl}&text=${shareText}`,
    email: `mailto:?subject=${shareText}&body=${shareUrl}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      toast.success(t("share.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-6 md:pt-10" data-testid="detail-page">
      <div className="flex items-center justify-between mb-8 gap-3">
        <Link
          to="/catalogo"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          data-testid="detail-back"
        >
          <ArrowLeft size={13} /> {t("detail.back")}
        </Link>
        <div className="flex items-center gap-1.5 text-xs">
          {item.prev ? (
            <Link
              to={`/catalogo/${item.prev.slug}`}
              className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 hover:border-foreground text-foreground"
              data-testid="detail-nav-prev"
              title={`${item.prev.artist} — ${item.prev.title}`}
            >
              <ChevronLeft size={12} /> Anterior
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-muted-foreground/40">
              <ChevronLeft size={12} /> Anterior
            </span>
          )}
          {item.next ? (
            <Link
              to={`/catalogo/${item.next.slug}`}
              className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 hover:border-foreground text-foreground"
              data-testid="detail-nav-next"
              title={`${item.next.artist} — ${item.next.title}`}
            >
              Siguiente <ChevronRight size={12} />
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-muted-foreground/40">
              Siguiente <ChevronRight size={12} />
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 lg:gap-20 items-start">
        <div className="md:sticky md:top-24">
          <div className="aspect-square bg-[hsl(var(--muted))] overflow-hidden">
            <img src={cover} alt={`${item.artist} — ${item.title}`} className="object-cover w-full h-full" data-testid="detail-cover" />
          </div>
        </div>

        <div className="flex flex-col gap-7">
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-muted-foreground" data-testid="detail-meta-line">
              {[item.label, item.catno, item.country, item.year].filter(Boolean).join(" · ")}
            </p>
            <h1 className="text-2xl md:text-3xl font-medium leading-tight text-foreground">
              <span className="text-muted-foreground">{item.artist}</span>{" — "}
              <span>{item.title}</span>
            </h1>
            {item.styles?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="detail-style-chips">
                {item.styles.slice(0, 4).map((s) => (
                  <Link
                    key={s}
                    to={`/generos/${encodeURIComponent(s)}`}
                    className="text-xs border border-border px-2 py-0.5 hover:border-foreground hover:text-[hsl(var(--accent))] transition-colors"
                    data-testid={`detail-style-${s}`}
                  >
                    {s}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-y-3 gap-x-8 border-t border-border pt-5 text-sm">
            <Spec label={t("detail.specs.format")} value={(item.format_details || []).join(", ") || item.format} />
            <Spec label={t("detail.specs.year")} value={item.year} />
            <Spec label={t("detail.specs.country")} value={item.country} />
            <Spec label={t("detail.specs.label")} value={item.label} />
            <Spec label={t("detail.specs.catno")} value={item.catno} />
            <Spec label={t("detail.specs.media")} value={item.media_condition} />
            <Spec label={t("detail.specs.sleeve")} value={item.sleeve_condition} />
            <Spec label={t("detail.specs.genres")} value={(item.genres || []).join(", ")} />
            {item.styles?.length > 0 && (
              <div className="col-span-2">
                <Spec label={t("detail.specs.styles")} value={item.styles.join(", ")} />
              </div>
            )}
          </dl>

          {item.comments && (
            <div className="border-t border-border pt-5">
              <p className="text-xs text-muted-foreground mb-2">{t("detail.notes")}</p>
              <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">{item.comments}</p>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-border pt-5">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">{t("detail.price")}</span>
              <span className="text-2xl font-medium tabular-nums" data-testid="detail-price">
                {formatPrice(item.price, item.currency)}
              </span>
            </div>
            <p className="text-xs text-[hsl(var(--accent))] tracking-wide" data-testid="detail-unique">
              · {t("detail.unique")}
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={cartHas(item.listing_id)}
                  onClick={() => {
                    cartAdd(item.listing_id);
                    toast.success("Añadido al carrito");
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-foreground text-background py-3.5 text-sm font-medium hover:bg-[hsl(var(--accent))] disabled:opacity-50 transition-colors"
                  data-testid="detail-add-to-cart"
                >
                  <ShoppingBag size={14} />
                  {cartHas(item.listing_id) ? "En el carrito" : "Añadir al carrito"}
                </button>
                <button
                  type="button"
                  onClick={() => toggle(item.listing_id)}
                  aria-label={fav ? "Quitar de favoritos" : "Añadir a favoritos"}
                  className="px-4 border border-border hover:border-foreground transition-colors"
                  data-testid="detail-fav-toggle"
                >
                  <Heart size={16} className={fav ? "fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" : ""} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!cartHas(item.listing_id)) cartAdd(item.listing_id);
                  navigate("/finalizar");
                }}
                className="w-full border border-border py-3 text-sm hover:border-foreground transition-colors"
                data-testid="detail-buy-now"
              >
                {lang === "en" ? "Order this record →" : "Pedir este disco →"}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("detail.safe")}{" "}
              <Link to="/envios" className="underline underline-offset-4 hover:text-foreground">{lang === "en" ? "Shipping" : "Envíos"}</Link>
            </p>
          </div>

          {/* Share */}
          <div className="border-t border-border pt-5" data-testid="detail-share">
            <p className="text-xs text-muted-foreground mb-3 inline-flex items-center gap-2">
              <Share2 size={12} /> {t("share.title")}
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" className="border border-border px-3 py-1.5 hover:border-foreground" data-testid="share-whatsapp">{t("share.whatsapp")}</a>
              <a href={shareLinks.twitter} target="_blank" rel="noreferrer" className="border border-border px-3 py-1.5 hover:border-foreground" data-testid="share-twitter">{t("share.twitter")}</a>
              <a href={shareLinks.telegram} target="_blank" rel="noreferrer" className="border border-border px-3 py-1.5 hover:border-foreground" data-testid="share-telegram">{t("share.telegram")}</a>
              <a href={shareLinks.email} className="border border-border px-3 py-1.5 hover:border-foreground" data-testid="share-email">{t("share.email")}</a>
              <button onClick={copyLink} className="border border-border px-3 py-1.5 hover:border-foreground inline-flex items-center gap-1.5" data-testid="share-copy">
                {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? t("share.copied") : t("share.copy")}
              </button>
            </div>
          </div>

          {videos.length > 0 && (
            <div className="border-t border-border pt-5" data-testid="detail-player-section">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted-foreground">{t("detail.player")} · {videoIdx + 1}/{videos.length}</p>
                {videos.length > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setVideoIdx((i) => (i - 1 + videos.length) % videos.length)} className="p-1.5 border border-border hover:border-foreground" data-testid="detail-player-prev" aria-label="Anterior"><ChevronLeft size={13} /></button>
                    <button onClick={() => setVideoIdx((i) => (i + 1) % videos.length)} className="p-1.5 border border-border hover:border-foreground" data-testid="detail-player-next" aria-label="Siguiente"><ChevronRight size={13} /></button>
                  </div>
                )}
              </div>
              <div className="aspect-video bg-black">
                {currentEmbed ? (
                  <iframe key={videos[videoIdx].uri} title={videos[videoIdx].title || "Preview"} src={currentEmbed} className="w-full h-full" allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <div className="w-full h-full grid place-items-center text-muted-foreground text-sm">{t("detail.videoUnavailable")}</div>
                )}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{videos[videoIdx].title}</p>
            </div>
          )}

          {tracklist.length > 0 && (
            <div className="border-t border-border pt-5" data-testid="detail-tracklist">
              <p className="text-xs text-muted-foreground mb-3">
                {t("detail.tracklist")}
                {videos.length > 0 && <span className="text-muted-foreground/70"> · pulsa una pista para preescuchar</span>}
              </p>
              <ul className="divide-y divide-border">
                {tracklist.map((tr, i) => {
                  // Find a matching video: 1) by title substring (either direction), 2) by index when counts match
                  let match = -1;
                  if (videos.length > 0 && tr.title) {
                    const trLow = tr.title.toLowerCase();
                    match = videos.findIndex((v) => {
                      const vt = (v.title || "").toLowerCase();
                      return vt.includes(trLow) || trLow.includes(vt.replace(/^.*?-\s*/, "").trim());
                    });
                    if (match < 0 && videos.length === tracklist.length) match = i;
                  }
                  const isPlaying = match >= 0 && match === videoIdx;
                  const clickable = match >= 0;
                  return (
                    <li key={`${tr.position || i}-${tr.title}`} className={`flex items-baseline justify-between gap-4 py-2 text-sm ${isPlaying ? "bg-[hsl(var(--muted))] -mx-2 px-2" : ""}`}>
                      <span className="text-muted-foreground text-xs min-w-[2rem] tabular-nums">{tr.position || String(i + 1).padStart(2, "0")}</span>
                      {clickable ? (
                        <button
                          type="button"
                          onClick={() => {
                            setVideoIdx(match);
                            document.querySelector('[data-testid="detail-player-section"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }}
                          className={`flex-1 text-left hover:text-[hsl(var(--accent))] inline-flex items-center gap-2 ${isPlaying ? "text-[hsl(var(--accent))]" : ""}`}
                          data-testid={`detail-track-play-${i}`}
                        >
                          <span className="flex-1 truncate">{tr.title}</span>
                          <span className="vmr-tiny">{isPlaying ? "▶ sonando" : "▶"}</span>
                        </button>
                      ) : (
                        <span className="flex-1 text-muted-foreground/70">{tr.title}</span>
                      )}
                      {tr.duration && <span className="text-xs text-muted-foreground tabular-nums">{tr.duration}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Spec({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}
